/* global window */
import { CartContext } from './context/CartContext';
import ColorSwatch from './ColorSwatch';
import { Form } from 'react-bootstrap';
import InputQuantity from './InputQuantity';
import { array, bool, func, number, object, oneOfType, string } from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import '../styles/ProductAddToCartForm.css';
import ProductOption from './ProductOption';
import AppContext from './context/AppContext';
import { ToastContext } from './context/ToastContext';
import constants from '../constants.json' assert { type: 'json' };
import CartAddedSuccessModal from './CartAddedSuccessModal';
import reason1 from '../images/reasons-1.png';
import reason2 from '../images/reasons-2.png';
import reason3 from '../images/reasons-3.png';
import reason4 from '../images/reasons-4.png';

const BUY_REASON_BANNER_CLASSNAME = 'buy-reason--banner';
const BUY_REASON_FIGURE_CLASSNAME = 'buy-reason--figure';

/**
 * Component for showing the "add to cart" form with or without options.
 * @component
 */
const ProductAddToCartForm = ({
    isAllOptionsSelected,
    isProductAvailableForSale,
    maxQuantity,
    options,
    price,
    selectedOptions,
    selectOption,
    selectedVariant,
    variants,
    totalCartQuantity,
}) => {
    /** Contexts  */
    const { appContext } = useContext(AppContext);
    const appConfig = appContext.config.themeConfig;
    const {
        addLineItem,
        cartState,
        cartUpdating,
        checkoutData,
        setShowCartOverlay,
    } = useContext(CartContext);
    const { sendMessage } = useContext(ToastContext);

    /** States */
    const [ quantity, setQuantity ] = useState(1);
    const [ showSuccessModal, setShowSuccessModal ] = useState(false);
    const [ addedProduct, setAddedProduct ] = useState(null);
    const [ productTotalQty, setProductTotalQty ] = useState(0);

    const defaultVariant = useMemo(
        () => variants.find(
            (variant) => (variant.title === 'Default Title'
                || variant.variantPosition === 1)
        ) ?? null,
        [ variants ],
    );

    const getSelectedOption = useCallback(
        (id) => selectedOptions.get(id) ?? '',
        [ selectedOptions ],
    );

    const hasOptions = useMemo(
        () => options.some(
            (option) => option.optionValue.split(', ').length > 1
        ),
        [ options ],
    );

    const isVariantAvailable = useMemo(() => {
        if (hasOptions) {
            if (selectedVariant) {
                return selectedVariant.availableForSale === true;
            }

            return defaultVariant && defaultVariant.availableForSale === true;
        }

        return isProductAvailableForSale === true;
    }, [isProductAvailableForSale, hasOptions, selectedVariant, defaultVariant]);


    // Create text for selected variant options.
    const selectedVariantText = useMemo(
        () => options.sort((a, b) => a.optionPosition - b.optionPosition)
            .map((option) => getSelectedOption(option.id))
            .filter((value) => value !== '').join(' / '),
        [ options ],
    );

    // Calculate price difference.
    const priceDiff = useMemo(
        () => !selectedVariant?.price || price === selectedVariant?.price
            ? 0
            : Math.round((selectedVariant.price - price) * 100) / 100,
        [ price, selectedVariant ],
    );
    const priceDiffText = useMemo(() => {
        if (priceDiff !== 0) {
            const sign = priceDiff > 0 ? '+' : '-';
            const amount = `$${Math.abs(priceDiff)} ${appConfig.storeCurrency}`;
            return ` [${sign}${amount}]`;
        }
        return '';
    }, [appConfig.storeCurrency, priceDiff]);


    const selectedVariantStatus = useMemo(() => {
        if (selectedVariant) {
            return isVariantAvailable ? '' : 'Out of stock';
        }
        if (!hasOptions || isAllOptionsSelected) {
            return 'Not available';
        }
        return 'Please select more options...';
    }, [selectedVariant, isVariantAvailable, hasOptions, isAllOptionsSelected]);


    /** Form Event Handlers */
    const prepareLineItems = useCallback((selectedVariant, defaultVariant, quantity, cartState) => {
        const availableInventoryLevels = selectedVariant
            ? selectedVariant.availableInventoryLevels ?? []
            : defaultVariant?.availableInventoryLevels ?? [];
        
        const lineItems = [];
        let quantityToAdd = quantity;
        
        for (const inventoryLevel of availableInventoryLevels) {
            const cartItem = cartState.find(
                (lineItem) => lineItem.inventoryLevelId === inventoryLevel.id
            );
            const currentQuantity = Math.min(
                quantityToAdd,
                (inventoryLevel.bypassNegativeQuantity === true
                    ? Infinity
                    : inventoryLevel.availableQuantity
                ) - (cartItem?.quantity ?? 0),
            );
            lineItems.push({
                inventoryLevelId: inventoryLevel.id,
                quantity: currentQuantity,
            });
            quantityToAdd -= currentQuantity;
            if (quantityToAdd <= 0) {
                break;
            }
        }
        
        return lineItems.filter((lineItem) => lineItem.quantity > 0);
    }, []);

    const addToCart = useCallback(async (lineItems, quantity) => {
        return lineItems.length > 1
            ? await addLineItem(null, null, lineItems, () => setQuantity(1))
            : await addLineItem(
                lineItems[0].inventoryLevelId,
                quantity,
                null,
                () => setQuantity(1),
            );
    }, [addLineItem, setQuantity]);

    const handleSuccessModal = useCallback((selectedVariant, defaultVariant, quantity, price, cartState) => {
        const variant = selectedVariant || defaultVariant;

        let variantInventoryLevelId = null;
        if (variant?.availableInventoryLevels?.length > 0) {
            variantInventoryLevelId = variant.availableInventoryLevels[0].id;
        }

        const productData = {
            title: variant?.title || 'Product',
            imageUrl: variant?.imageUrl ||
                (variant?.Images && variant.Images.length > 0 ? variant.Images[0].url : null),
            price: variant?.price || price,
            inventoryLevelId: variantInventoryLevelId
        };

        let productTotalQuantity = quantity;

        if (variant?.availableInventoryLevels) {
            const variantInventoryLevelIds = variant.availableInventoryLevels.map((level) => level.id);

            cartState.forEach((item) => {
                if (variantInventoryLevelIds.includes(item.inventoryLevelId)) {
                    productTotalQuantity += item.quantity;
                }
            });
        }

        productTotalQuantity = Math.max(quantity, productTotalQuantity);

        setAddedProduct(productData);
        setProductTotalQty(productTotalQuantity);
        setShowSuccessModal(true);
    }, [setAddedProduct, setProductTotalQty, setShowSuccessModal]);

    const shouldAddToCart = useCallback((checkout, totalCartQuantity, selectedVariant, defaultVariant) => {
        return checkout === false
            || (totalCartQuantity < 1
                && (selectedVariant
                    || defaultVariant.availableForSale === true
                )
            );
    }, []);

    const handleCheckout = useCallback((result, checkoutData, sendMessage) => {
        if ((!result || result?.success === true) && checkoutData.webUrl) {
            return window.location.assign(checkoutData.webUrl);
        }
        sendMessage(
            'We couldn\'t redirect you to the checkout page right now. '
                + 'Please try using the cart icon in the top right corner '
                + 'of the page.',
            'info',
        );
    }, []);

    const handleFormSubmission = useCallback(async (e, checkout = false) => {
        e.preventDefault();
        e.stopPropagation();
        
        let result;
        
        if (shouldAddToCart(checkout, totalCartQuantity, selectedVariant, defaultVariant)) {
            const lineItems = prepareLineItems(selectedVariant, defaultVariant, quantity, cartState);
            result = await addToCart(lineItems, quantity);
        }
        
        if (result?.success === false) {
            return sendMessage(
                result.error ?? 'There was a problem adding the item to the '
                    + 'cart. Please try again later.',
                'danger',
            );
        }

        // Show success modal instead of cart overlay
        if (!checkout && result?.success) {
            handleSuccessModal(selectedVariant, defaultVariant, quantity, price, cartState);
            return;
        }

        if (checkout === true) {
            handleCheckout(result, checkoutData, sendMessage);
            return;
        }
        
        setShowCartOverlay(true);
    }, [
        shouldAddToCart,
        prepareLineItems,
        addToCart,
        handleSuccessModal,
        handleCheckout,
        totalCartQuantity,
        selectedVariant,
        defaultVariant,
        quantity,
        cartState,
        sendMessage,
        price,
        checkoutData,
        setShowCartOverlay,
    ]);

    // Calculate cart subtotal
    const cartSubtotal = useMemo(() => cartState.reduce((total, item) => total + (item.price * item.quantity), 0), [cartState]);

    // Calculate total items in cart
    const totalCartItems = useMemo(() => cartState.reduce((total, item) => total + item.quantity, 0), [cartState]);

    // Handle closing the success modal
    const handleCloseModal = useCallback(() => {
        setShowSuccessModal(false);
    }, []);
    // Handle continue shopping button
    const handleContinueShopping = useCallback(() => {
        setShowSuccessModal(false);
    }, []);
    // Handle view cart button
    const handleViewCart = useCallback(() => {
        setShowSuccessModal(false);
        setShowCartOverlay(true);
    }, [setShowCartOverlay]);

    /** Get global cart quantity (of all items) */
    const globalCartQty = useMemo(() => cartState
        ?.map((item) => item.quantity)
        .reduce((prev, cur) => +prev + +cur, 0)
    ?? 0, [cartState]);

    /** Get variants that overlap with the selected options */
    const variantsWithSelectedOptions = useCallback((excludeLastOption = false) => {
        const resultVariants = [];
        const selectedOptsArr = [...selectedOptions.entries()];
        if (excludeLastOption === true) {
            selectedOptsArr.splice(-1, 1);
        }
        for (const variant of variants) {
            let isMatch = true;
            for (const [optId, optValue] of selectedOptsArr) {
                const match = variant.Options.find((vOpt) => vOpt.id === optId);
                if (!match || match.VariantSelectedOptions.optionValueSelected !== optValue) {
                    isMatch = false;
                    break;
                }
            }
            if (isMatch) {
                resultVariants.push(variant);
            }
        }
        return resultVariants;
    }, [variants, selectedOptions]);


    /* Check if the option Id provided is the last on the selected options */
    const isLastOptionFromSelected = useCallback((optionId) => {
        const selectedOptsArr = [...selectedOptions.keys()];
        return (selectedOptsArr.at(-1) === optionId);
    }, [selectedOptions]);

    /* Returns true if there are no selections */
    const allOptionsEmpty = useMemo(
        () => options.every((o) => getSelectedOption(o.id) === ''),
        [options, getSelectedOption]
    );

    const handleColorSwatchClick = useCallback(
        (optionId, value) => () => {
            selectOption(optionId, value);
        }, [selectOption]);


    /* Render the layout */
    return <Form
        className={'addtocart--form'}
        onSubmit={handleFormSubmission}>
        {(selectedOptions.size > 0 || selectedVariant)
            && <div
                className={'addtocart--variant-info'}>
                <span>
                    {selectedVariant?.title === constants.DEFAULT_VARIANT_TITLE ? (
                        null
                    ) : <span>{(isVariantAvailable ? 'Variant' : 'You selected')}:&nbsp;</span> }
                    {selectedVariant && selectedVariant.title !== constants.DEFAULT_VARIANT_TITLE && (
                        <span>{selectedVariant.title}</span>
                    )}
                    {!selectedVariant && (
                        <span>{selectedVariantText}</span>
                    )}
                    {priceDiffText}
                </span>
                {(isVariantAvailable
                    && selectedVariant?.InventoryItem.sku)
                    && <span
                        className={'addtocart--product-sku'}>
                        <strong>SKU</strong>: {selectedVariant.InventoryItem.sku}
                    </span>
                }
                {selectedVariantStatus !== ''
                    && <span
                        className={`addtocart--product-status${selectedVariantStatus === 'Not available' ? ' unavailable' : ''}`}>
                        {selectedVariantStatus}
                    </span>}
            </div>
        }
        {!(options.length === 1
            && options[0].optionName === 'Title'
            && options[0].optionValue === 'Default Title'
        ) && options
            .sort((a, b) => a.optionPosition - b.optionPosition)
            .map((option, index) => option.optionName.toLowerCase() === 'color'
                ? <div
                    className={'addtocart--option'}
                    key={`${option.optionName}-${index}`}>
                    <span>Color</span>
                    <div
                        className={'addtocart--horizontal-option-list'}>
                        {option.optionValue
                            .split(', ')
                            .map((value, index) => <ColorSwatch
                                key={`${option.optionName}-${index}`}
                                colorName={value}
                                size={'3rem'}
                                isActive={getSelectedOption(option.id) === value}
                                onClick={handleColorSwatchClick(option.id, value)}
                            />)
                        }
                    </div>
                </div>
                : <div
                    key={`${option.optionName}-${index}`}
                    className={'addtocart--option'}>
                    <ProductOption
                        option={option}
                        allOptionsEmpty={allOptionsEmpty}
                        variantsWithSelectedOptions={variantsWithSelectedOptions}
                        isLastOptionFromSelected={isLastOptionFromSelected}
                        selectOption={selectOption}
                        defaultValue={getSelectedOption(option.id)} />
                </div>
            )
        }
        <hr
            className={'product--divider'}
        />
        <div
            className={'addtocart--submit-section'}>
            <div
                style={{ width: '100%' }}
            >
                <span
                    className={'quantity--label'}
                >
                    Quantity:
                </span>
                <InputQuantity
                    max={maxQuantity}
                    quantity={quantity}
                    setQuantity={setQuantity}
                    disabled={price === 0.0}
                />
            </div>
            <button
                className={'addtocart--submit-button'}
                type={'submit'}
                disabled={(cartUpdating || maxQuantity < 1 || ((hasOptions && !isAllOptionsSelected) && !isVariantAvailable)) || price === 0.0}>
                {
                    selectedVariant && maxQuantity > 0 && 'Add to cart'
                }
                {
                    selectedVariant && maxQuantity <= 0 && 'Out of stock'
                }
                {
                    !selectedVariant && 'Add to cart.'
                }
            </button>
            <button
                className={'addtocart--submit-button'}
                disabled={(cartUpdating || !isVariantAvailable) || price === 0.0}
                type={'submit'}
                onClick={(e) => (globalCartQty || (isVariantAvailable && (!hasOptions || isAllOptionsSelected))) ? handleFormSubmission(e, true) : undefined}
            >Buy Now</button>
        </div>

        <div
            className={'buy-reasons--section'}
        >
            <h5
                className={'buy-reasons--title'}
            >
                <strong>4 GREAT REASONS TO BUY FROM US:</strong>
            </h5>
            <div
                className={'buy-reasons--content'}
            >
                <div
                    className={BUY_REASON_BANNER_CLASSNAME}
                >
                    <div
                        className={BUY_REASON_FIGURE_CLASSNAME}
                    >
                        <img
                            className={'img-fluid'}
                            src={reason1}
                            alt={'100% Satisfaction Guaranteed'}
                        />
                    </div>
                </div>
                <div
                    className={BUY_REASON_BANNER_CLASSNAME}
                >
                    <div
                        className={BUY_REASON_FIGURE_CLASSNAME}
                    >
                        <img
                            className={'img-fluid'}
                            src={reason2}
                            alt={'Fast Shipping'}
                        />
                    </div>
                </div>
                <div
                    className={BUY_REASON_BANNER_CLASSNAME}
                >
                    <div
                        className={BUY_REASON_FIGURE_CLASSNAME}
                    >
                        <img
                            className={'img-fluid'}
                            src={reason3}
                            alt={'Secure Ordering'}
                        />
                    </div>
                </div>
                <div
                    className={BUY_REASON_BANNER_CLASSNAME}
                >
                    <div
                        className={BUY_REASON_FIGURE_CLASSNAME}
                    >
                        <img
                            className={'img-fluid'}
                            src={reason4}
                            alt={'Easy Returns'}
                        />
                    </div>
                </div>
            </div>
        </div>

        <CartAddedSuccessModal
            show={showSuccessModal}
            onClose={handleCloseModal}
            product={addedProduct}
            quantity={productTotalQty}
            justAdded={quantity}
            subtotal={cartSubtotal}
            totalItems={totalCartItems}
            onContinueShopping={handleContinueShopping}
            onViewCart={handleViewCart}
            checkoutUrl={checkoutData.webUrl}
        />
    </Form>;
};

ProductAddToCartForm.propTypes = {
    isAllOptionsSelected: bool,
    isProductAvailableForSale: bool,
    maxQuantity: number,
    options: array,
    price: oneOfType([number, string]),
    selectedOptions: object,
    selectOption: func,
    selectedVariant: object,
    variants: array,
    totalCartQuantity: number,
};

export default ProductAddToCartForm;
